import { useEffect, useRef, useState } from "react";
import { Button, Input, message, Table, Popconfirm } from "antd";
import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { hasSubstring } from "../../../extensions/helper";
import { serverFetch } from "../../../server";
import ReportDesign from "./report-design";
import { dictsReportVidName } from "../../../extensions/dicts";
import "./report-designs.css";

const ReportDesigns = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState();
  const [filter, setFilter] = useState({ searchText: "" });
  const divRef = useRef(null);

  const getData = () => {
    setLoading(true);
    serverFetch("reportdesign")
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка загрузки данных");
      })
  }

  useEffect(getData, []);

  const onDelete = (id) => {
    setLoading(true);
    serverFetch(`reportdesign/${id}`, { method: "DELETE" })
      .then(() => {
        message.success("Запись удалена");
        getData();
      })
      .catch(e => {
        setLoading(false);
      })
  }

  const onImport = () => {
    // Создаем скрытый input для выбора файлов
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.multiple = true;
    
    input.onchange = async (event) => {
      const files = Array.from(event.target.files);
      
      if (files.length === 0) {
        return;
      }

      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      try {
        // Обрабатываем каждый файл
        for (const file of files) {
          try {
            // Читаем содержимое файла
            const text = await readFileAsText(file);
            
            // Парсим JSON
            const reportData = JSON.parse(text);
            
            // Подготавливаем данные для отправки
            const dataToSend = {
              ...reportData,
              id: 0, // Устанавливаем id = 0 для создания новой записи
              body: JSON.stringify(reportData.body || []) // Сериализуем body
            };
            
            // Отправляем на сервер
            await serverFetch("reportdesign", {
              method: "POST",
              bodyData: dataToSend
            });
            
            successCount++;
          } catch (error) {
            console.error(`Ошибка импорта файла ${file.name}:`, error);
            errorCount++;
          }
        }
        
        // Показываем результат
        if (successCount > 0) {
          message.success(`Успешно импортировано ${successCount} файлов`);
          getData(); // Обновляем список
        }
        
        if (errorCount > 0) {
          message.error(`Ошибка импорта ${errorCount} файлов`);
        }
        
      } catch (error) {
        console.error("Общая ошибка импорта:", error);
        message.error("Ошибка при импорте файлов");
      } finally {
        setLoading(false);
      }
    };
    
    // Запускаем выбор файлов
    input.click();
  };

  // Вспомогательная функция для чтения файла как текст
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 80 },
    { title: "Наименование", dataIndex: "name" },
    { title: "Описание", dataIndex: "description" },
    {  title: "Вид", dataIndex: "vid",  align: "center", render: dictsReportVidName },
    { title: "Компания", dataIndex: "companyId", width: 120, align: "center" },
    {
      title: "Элементов",
      dataIndex: "body",
      width: 100,
      align: "center",
      render: (body) => {
        if (!body) return 0;
        try {
          const parsed = typeof body === 'string' ? JSON.parse(body) : body;
          return Array.isArray(parsed) ? parsed.length : 0;
        } catch (e) {
          return 0;
        }
      }
    },
    {
      title: "Действия",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Удалить запись?"
          onConfirm={() => onDelete(record.id)}
          okText="Да"
          cancelText="Нет"
        >
          <DeleteOutlined style={{ color: "red", cursor: "pointer", fontSize: 16 }} />
        </Popconfirm>
      )
    }
  ]

  const onClose = (refresh) => {
    setEditId(null);
    refresh && getData();
  }

  const onAdd = () => {
    setEditId(0);
  }

  const divItem = editId != null && <ReportDesign id={editId} onClose={onClose} />
  const headerSize = divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) || 0;

  const onChangeFilter = value => setFilter({ ...filter, ...value });

  const filtered = items.filter(item => {
    if (!hasSubstring(filter.searchText, item.name, item.description)) return false;
    return true;
  })

  return <div className="col-one-main sport-form-content">
    <div className="sport-form-title">
      <Input 
        placeholder="Поиск" 
        value={filter.searchText} 
        onChange={e => onChangeFilter({ searchText: e.target.value })} 
        allowClear 
        style={{ maxWidth: 300 }}
      />
      <Button onClick={getData}>Обновить</Button>
      <Button type="primary" onClick={onAdd}>Добавить</Button>
      <Button icon={<UploadOutlined />} onClick={onImport} loading={loading}>
        Импорт
      </Button>
    </div>
    <div ref={divRef} style={{ width: tableWidth }}>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ y: tableHeight }}
        rowSelection={{ 
          type: 'radio', 
          selectedRowKeys: [editId], 
          renderCell: checked => <div className={checked ? "list-indicator" : ""} /> 
        }}
        onRow={(record) => ({ onClick: () => { setEditId(record.id) } })}
      />
    </div>
    {divItem}
  </div>
}

export default ReportDesigns;